import moment from 'moment-timezone' // Updated to use moment-timezone
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { CSSTransition } from 'react-transition-group'

import { isPaidUser } from 'utils/freeVersionBlock'

import { useGrowthBookFeature } from '../../../../context/growthbook-context'
import { selectCurrentUser } from '../../../../state/selectors'
import { growthbookFeatures } from '../../../../utils/growthbook'
import { getLanguageCode } from '../../../../utils/translation'
import { isWithinDateRange } from '../../../../utils/isWithinDateRange'
import { TIME_ZONE } from '../consts'

import { AppBannerButton } from './AppBannerButton'
import { AppBannerCounter } from './AppBannerCounter'
import { AppBannerCross } from './AppBannerCross'
import { AppBannerRibbonIcon } from './AppBannerRibbonIcon'
import { AppBannerText } from './AppBannerText'
import {
  AppBannerContainer,
  AppBannerControls,
  AppBannerWrapper,
} from './styled'
import { Snowfall } from './Snowfall'

export const HIDE_ON_PAGES = [
  'pricing',
  'register',
  'login',
  'forgot-password',
  'checkout',
  'settings',
]

const APP_BANNER_LOCAL_KEY = 'appbnrh'

export const AppBanner = ({ hideButton, textBanner = null }) => {
  const {
    isEnabled: isGrowthbookFeatureOn,
    value: featureValue,
    theme: featureTheme,
    initializeFeatures,
  } = useGrowthBookFeature(growthbookFeatures.PROMO_OFFER)

  const [dates, setDates] = useState({ start: null, end: null })
  const [isFeatureOn, setIsFeatureOn] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [bannerTheme, setBannerTheme] = useState(
    featureTheme?.appBanner || null,
  )

  const user = useSelector(selectCurrentUser())
  const lang = getLanguageCode()

  const onTimeOver = () => {
    setIsOpen(false)
    initializeFeatures()
  }

  useEffect(() => {
    if (featureTheme?.appBanner && !bannerTheme) {
      setBannerTheme(featureTheme.appBanner)
    }
  }, [featureTheme?.appBanner])

  useEffect(() => {
    if (featureValue?.start_date && featureValue?.end_date) {
      setDates({
        start: moment.tz(featureValue.start_date, TIME_ZONE).toDate(),
        end: moment.tz(featureValue.end_date, TIME_ZONE).toDate(),
      })
    }
  }, [featureValue?.start_date, featureValue?.end_date])

  useEffect(() => {
    if (!dates.start || !dates.end) return

    const isWithinPromoPeriod = isWithinDateRange({
      startDate: dates.start,
      endDate: dates.end,
      timeZone: TIME_ZONE,
    })

    setIsFeatureOn(isWithinPromoPeriod && isGrowthbookFeatureOn)
  }, [user, isGrowthbookFeatureOn, dates?.start, dates?.end])

  useEffect(() => {
    if (
      !isWithinDateRange({
        startDate: dates.start,
        endDate: dates.end,
        timeZone: TIME_ZONE,
      }) ||
      !isFeatureOn
    ) {
      setIsOpen(false)
      return
    }

    const storedAppBannerDate = localStorage.getItem(APP_BANNER_LOCAL_KEY)

    if (!storedAppBannerDate) {
      setIsOpen(true)
      return
    }

    const currentDate = moment.tz(TIME_ZONE) // Current date in PST
    const lastBannerDate = moment.tz(storedAppBannerDate, TIME_ZONE) // Parse in PST
    const nextBannerDisplayDate = lastBannerDate.add(1, 'days')
    if (nextBannerDisplayDate.isBefore(currentDate)) setIsOpen(true)
  }, [isFeatureOn])

  const onClose = () => {
    setIsOpen(false)
    localStorage.setItem(APP_BANNER_LOCAL_KEY, moment.tz(TIME_ZONE).toDate()) // Store current PST time
  }

  if (!bannerTheme) return null

  return (
    <CSSTransition in={isOpen} timeout={0} unmountOnExit>
      <div
        className="tw-relative tw-flex tw-justify-center tw-items-center tw-overflow-hidden"
        style={{
          background: bannerTheme.background,
        }}
      >
        {bannerTheme?.bgAnimation === 'snowfall' && <Snowfall />}
        <AppBannerContainer
          textBanner={textBanner}
          lang={lang}
          theme={bannerTheme}
          isCrossHidden={!isPaidUser()}
        >
          <AppBannerRibbonIcon lang={lang} theme={bannerTheme} />
          <AppBannerWrapper>
            <AppBannerText textBanner={textBanner} theme={bannerTheme} />
            <AppBannerControls textBanner={textBanner}>
              {!hideButton && (
                <AppBannerButton user={user} theme={bannerTheme} />
              )}
              <AppBannerCounter
                textBanner={textBanner}
                endDate={dates.end}
                theme={bannerTheme}
                onTimeOver={onTimeOver}
              />
            </AppBannerControls>
          </AppBannerWrapper>
          {isPaidUser() && (
            <AppBannerCross onClose={onClose} theme={bannerTheme} />
          )}
        </AppBannerContainer>
      </div>
    </CSSTransition>
  )
}
